import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { axiosInstance } from "@/pages/auth/auth.context";
import { User } from "../types";
import {
  employeeFormSchema,
  EmployeeFormValues,
} from "../schemas/employeeFormSchema";
import { UserRole } from "@/pages/auth/types";
import { ShirtSize, getAllShirtSizes } from "@/types/shirtSizes";

export const useEmployeeForm = (
  employeeId: string | undefined,
  employeeData: User | undefined,
) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState<boolean>(false);
  const form = useForm<EmployeeFormValues>({
    resolver: zodResolver(employeeFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      city: null,
      seniorityLevelId: "",
      eligiblePositionIds: [],
      taxDocumentsSigned: false,
      positionDocuments: [],
      role: UserRole.USER,
      emailNotifications: false,
      smsNotifications: false,
      bilingual: false,
      userNotes: "",
      referredById: null,
    },
  });

  const setFormValues = () => {
    if (employeeData) {
      form.reset({
        firstName: employeeData.firstName,
        lastName: employeeData.lastName,
        email: employeeData.email,
        phoneNumber: employeeData.phoneNumber,
        city: employeeData.city || null,
        seniorityLevelId: employeeData.seniorityLevelId,
        eligiblePositionIds: employeeData.eligiblePositionIds || [],
        taxDocumentsSigned: employeeData.taxDocumentsSigned || false,
        positionDocuments: employeeData.positionDocuments || [],
        role: employeeData.role || UserRole.USER,
        emailNotifications: employeeData.emailNotifications,
        smsNotifications: employeeData.smsNotifications,
        bilingual: employeeData.bilingual || false,
        status: employeeData.status,
        howDidYouHearAboutUs: employeeData.howDidYouHearAboutUs || "",
        shirtSize: Object.values(ShirtSize).includes(
          employeeData.shirtSize as ShirtSize,
        )
          ? (employeeData.shirtSize as ShirtSize)
          : ShirtSize.SMALL,
        receivedShirt: employeeData.receivedShirt || false,
        badgeReceived: employeeData.badgeReceived || false,
        userNotes: employeeData.userNotes || "",
        referredById: employeeData.referredById ?? null,
      });
    }
  };
  const onSubmit = async (values: EmployeeFormValues) => {
    if (!employeeId) return;

    setLoading(true);
    try {
      await axiosInstance.put(`user/${employeeId}`, values); // this was payload.

      toast({
        title: "Success!",
        description: "Employee information updated successfully.",
        variant: "success",
      });
      setTimeout(() => {
        navigate("/admin/employees");
      }, 1000);
    } catch (error) {
      console.error("ERROR SUBMITTING FORM", error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again later. " + error,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    form,
    loading,
    setFormValues,
    onSubmit,
  };
};
